import React, { useCallback } from 'react'
import { ColDef, ColGroupDef, GridOptions, ICellEditorParams } from 'ag-grid-community'
import { Locale, Translation } from 'locale'
import { CollateralBasket, CollateralBasketStatus } from 'api'
import { AgGridWrapper, gridOptionsBase, groupingRowColDef, TypedCellClassParams, TypedValueGetterParams } from 'view/components/datagrid'
import { CollateralBasketsPanelGridRow } from './CollateralBasketsPanelGridModel'
import { OnOffButtons, OnOffGroupButtons } from 'view/components/buttons'

export interface CollateralsPanelGridProps {
  rowData: CollateralBasketsPanelGridRow[]
  disabled?: boolean
  onUpdateDirectClearing: (collateralIds: string[], enabled: boolean) => void
  onUpdateSponsoredClearing: (collateralIds: string[], enabled: boolean) => void
}

const noCollateralBasketsPlaceholder = () => <div>{Locale.translate(Translation.SC_NO_BASKETS_AVAILABLE)}</div>

const applySuspendedClass = (params: TypedCellClassParams<CollateralBasketsPanelGridRow>) =>
  params.data?.collateralBasket.status === CollateralBasketStatus.SUSPENDED

const directClearingCellRenderer = React.forwardRef((params: ICellEditorParams, ref: React.Ref<any>) => {
  const allLeafChildren = params.node.allLeafChildren ?? []

  return params.node.group ? (
    <OnOffGroupButtons
      ref={ref}
      on={allLeafChildren.map(node => node.data.directClearingEnabled)}
      onLabel={Translation.UC_ON}
      offLabel={Translation.UC_OFF}
      onChange={on =>
        params.context.onUpdateDirectClearingEnabled(
          allLeafChildren.map(node => node.data.collateralBasket),
          on
        )
      }
      disabled={params.context.disabled}
    />
  ) : (
    <OnOffButtons
      ref={ref}
      on={params.node.data.directClearingEnabled}
      onLabel={Translation.UC_ON}
      offLabel={Translation.UC_OFF}
      onChange={on => params.context.onUpdateDirectClearingEnabled([params.node.data.collateralBasket], on)}
      disabled={params.context.disabled}
    />
  )
})

const sponsoredClearingCellRenderer = React.forwardRef((params: ICellEditorParams, ref: React.Ref<any>) => {
  const allLeafChildren = params.node.allLeafChildren ?? []

  return params.node.group ? (
    <OnOffGroupButtons
      ref={ref}
      on={allLeafChildren.map(node => node.data.sponsoredClearingEnabled)}
      onLabel={Translation.UC_ON}
      offLabel={Translation.UC_OFF}
      onChange={on =>
        params.context.onUpdateSponsoredClearingEnabled(
          allLeafChildren.map(node => node.data.collateralBasket),
          on
        )
      }
      disabled={params.context.disabled}
    />
  ) : (
    <OnOffButtons
      ref={ref}
      on={params.node.data.sponsoredClearingEnabled}
      onLabel={Translation.UC_ON}
      offLabel={Translation.UC_OFF}
      onChange={on => params.context.onUpdateSponsoredClearingEnabled([params.node.data.collateralBasket], on)}
      disabled={params.context.disabled}
    />
  )
})

const columnDefs: (ColDef | ColGroupDef)[] = [
  {
    ...groupingRowColDef,
    headerName: Locale.translate(Translation.TC_SECTOR),
    width: 150,
    showRowGroup: 'BASKET_SECTOR',
    headerComponent: undefined,
    pinned: false,
    resizable: true,
    hide: false,
    sort: 'asc',
    sortable: false
  },
  {
    colId: 'BASKET_SECTOR',
    valueGetter: (params: TypedValueGetterParams<CollateralBasketsPanelGridRow>) => params.data?.productSector?.name,
    rowGroup: true,
    hide: true
  },
  {
    colId: 'BASKET_NAME',
    valueGetter: (params: TypedValueGetterParams<CollateralBasketsPanelGridRow>) => params.data?.collateralBasket.name,
    cellClass: 'clearing-house-dialog-grid-column',
    cellClassRules: { suspended: applySuspendedClass },
    headerName: Locale.translate(Translation.TC_BASKET_NAME),
    suppressMovable: true,
    resizable: true,
    sort: 'asc',
    sortable: false,
    minWidth: 200
  },
  {
    colId: 'BASKET_ISIN',
    valueGetter: (params: TypedValueGetterParams<CollateralBasketsPanelGridRow>) => params.data?.collateralBasket.collateralBasketIsin,
    cellClass: 'clearing-house-dialog-grid-column',
    cellClassRules: { suspended: applySuspendedClass },
    headerName: Locale.translate(Translation.TC_ISIN),
    suppressMovable: true,
    resizable: true,
    sortable: false,
    minWidth: 150
  },
  {
    colId: 'DIRECT_CLEARING_ENABLED',
    valueGetter: (params: TypedValueGetterParams<CollateralBasketsPanelGridRow>) => params.data?.directClearingEnabled,
    headerName: Locale.translate(Translation.TC_DIRECT_CLEARING),
    width: 110,
    suppressMovable: true,
    resizable: true,
    sortable: false,
    aggFunc: () => NaN,
    cellRenderer: directClearingCellRenderer
  },
  {
    colId: 'SPONSORED_CLEARING_ENABLED',
    valueGetter: (params: TypedValueGetterParams<CollateralBasketsPanelGridRow>) => params.data?.sponsoredClearingEnabled,
    headerName: Locale.translate(Translation.TC_SPONSORED_CLEARING),
    width: 140,
    suppressMovable: true,
    resizable: true,
    sortable: false,
    aggFunc: () => NaN,
    cellRenderer: sponsoredClearingCellRenderer
  }
]

const gridOptions: GridOptions = {
  ...gridOptionsBase,
  groupDefaultExpanded: 0,
  getRowId: ({ data }) => data.collateralBasket.id
}

export const CollateralBasketsPanelGrid: React.FunctionComponent<CollateralsPanelGridProps> = props => {
  const { onUpdateDirectClearing, onUpdateSponsoredClearing, rowData, disabled } = props

  const onUpdateDirectClearingEnabled = useCallback(
    (collateralBaskets: CollateralBasket[], enabled: boolean) => {
      const updatedCollateralBasketIds = collateralBaskets.map(collateralBasket => collateralBasket.id)
      onUpdateDirectClearing(updatedCollateralBasketIds, enabled)
    },
    [onUpdateDirectClearing]
  )

  const onUpdateSponsoredClearingEnabled = useCallback(
    (collateralBaskets: CollateralBasket[], enabled: boolean) => {
      const updatedCollateralBasketIds = collateralBaskets.map(collateralBasket => collateralBasket.id)
      onUpdateSponsoredClearing(updatedCollateralBasketIds, enabled)
    },
    [onUpdateSponsoredClearing]
  )

  return (
    <AgGridWrapper
      className="datagrid-borderless"
      gridOptions={gridOptions}
      columnDefs={columnDefs}
      rowData={rowData}
      context={{
        onUpdateDirectClearingEnabled,
        onUpdateSponsoredClearingEnabled,
        disabled
      }}
      placeholder={noCollateralBasketsPlaceholder}
    />
  )
}
